// Página /eventos — listado completo público.
// Muestra: próximos eventos (cards activas) + ediciones anteriores (cards desactivadas).

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Calendar, MapPin, Users, ArrowRight, Sparkles,
} from 'lucide-react';
import { getEventos } from '../lib/api';

function formatEuros(n) {
  if (n == null) return '';
  const num = Number(n);
  if (Number.isInteger(num)) return `${num} €`;
  return `${num.toFixed(2).replace('.', ',')} €`;
}

function formatFechaCorta(iso, lang) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  const map = { es: 'es-ES', en: 'en-GB', ru: 'ru-RU', fr: 'fr-FR' };
  return new Intl.DateTimeFormat(map[lang] || 'es-ES', {
    day: 'numeric', month: 'long', year: 'numeric',
  }).format(d);
}

function EventoCardListado({ evento, pasado }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const lleno = evento.plazas_restantes === 0;
  const inner = (
    <>
      <div className="evento-card-foto">
        {evento.imagen_url ? (
          <img src={evento.imagen_url} alt={evento.nombre} />
        ) : (
          <div className="evento-card-foto-placeholder">
            <Sparkles size={32} strokeWidth={1.2} />
          </div>
        )}
        {pasado && <div className="evento-card-badge evento-card-badge--pasado">{t('eventos.finalizado')}</div>}
        {!pasado && lleno && <div className="evento-card-badge">{t('eventos.lleno')}</div>}
      </div>
      <div className="evento-card-body">
        <div className="evento-card-fecha"><Calendar size={14} strokeWidth={1.5} /> {formatFechaCorta(evento.fecha, lang)} · {evento.hora_inicio}</div>
        <h3 className="evento-card-title">{evento.nombre}</h3>
        <p className="evento-card-desc">{evento.descripcion}</p>
        <div className="evento-card-meta">
          <span><MapPin size={12} strokeWidth={1.5} /> {evento.lugar}</span>
          {!pasado && <span><Users size={12} strokeWidth={1.5} /> {evento.plazas_restantes} / {evento.aforo_maximo}</span>}
        </div>
        {!pasado && (
          <div className="evento-card-cta">
            {evento.precio_euros > 0
              ? <span className="evento-card-precio">{formatEuros(evento.precio_euros)}</span>
              : <span className="evento-card-gratis">{t('eventos.gratis')}</span>}
            <span className="evento-card-link">{t('eventos.ver_detalles')} <ArrowRight size={14} strokeWidth={1.5} /></span>
          </div>
        )}
      </div>
    </>
  );
  if (pasado) {
    return <article className="evento-card evento-card--pasado">{inner}</article>;
  }
  return (
    <Link to={`/eventos/${evento.slug}`} className="evento-card evento-card--link">
      {inner}
    </Link>
  );
}

export default function Eventos() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setData(null); setError(null);
    getEventos(lang)
      .then((r) => { if (!cancelled) setData(r); })
      .catch((e) => { if (!cancelled) setError(e); });
    return () => { cancelled = true; };
  }, [lang]);

  return (
    <main className="eventos-page">
      <div className="eventos-page-inner">
        <header className="eventos-page-head">
          <span className="section-eyebrow">{t('eventos.eyebrow')}</span>
          <h1 className="section-title">{t('eventos.page_titulo')}</h1>
          <p className="eventos-page-sub">{t('eventos.page_sub')}</p>
        </header>

        {error ? (
          <div className="clases-error">{t('eventos.error_carga')}</div>
        ) : !data ? (
          <div className="eventos-grid">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="evento-card evento-skeleton" aria-hidden="true" />
            ))}
          </div>
        ) : (
          <>
            <section className="eventos-bloque">
              <h2 className="eventos-bloque-titulo">{t('eventos.proximos')}</h2>
              {data.proximos.length === 0 ? (
                <p className="eventos-empty">{t('eventos.proximamente')}</p>
              ) : (
                <div className="eventos-grid">
                  {data.proximos.map((e) => <EventoCardListado key={e.id} evento={e} pasado={false} />)}
                </div>
              )}
            </section>

            {data.pasados.length > 0 && (
              <section className="eventos-bloque eventos-bloque--pasados">
                <h2 className="eventos-bloque-titulo">{t('eventos.ediciones_anteriores')}</h2>
                <div className="eventos-grid">
                  {data.pasados.map((e) => <EventoCardListado key={e.id} evento={e} pasado={true} />)}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </main>
  );
}
